import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import BaseCurrency from "../Common/BaseCurrency";
import { millify } from "../../config/helper";
import eyeOpen from "../../assets/images/eye-open.svg";
import eyeClose from "../../assets/images/eye-close.svg";

const EyeComponent = ({ image }) => (
  <span
    style={{
      cursor: "pointer",
      marginLeft: 5,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <img src={image} alt="eye" style={{ width: 16, height: 16 }} />
  </span>
);

export default function HorizontalCard({ card, sensitiveData }) {
  const history = useHistory();
  const [showBal, setShowBal] = useState(false);
  const [visibleField, setVisibleField] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  const userData = useSelector(
    (state) => state.cards.profile?.data?.response?.profile
  );

  const formatNumber = (number = "") =>
    number.replace(/(.{4})/g, "$1 ").trim();

  const maskCardNumber = (num) =>
    num ? `XXXX XXXX XXXX ${num.slice(-4)}` : "-";

  const maskExpiryDate = () => "XX/XX";
  const maskCVV = () => "XXX";

  const formatExpiry = (date) =>
    date ? `${date.slice(0, 2)}/${date.slice(-2)}` : "";

  useEffect(() => {
    if (!imageSrc && userData?.cards?.data?.imageUrl) {
      setImageSrc(userData.cards.data.imageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.cards?.data?.imageUrl]);

  useEffect(() => {
    const imageUrl = imageSrc || userData?.cards?.data?.imageUrl || card?.imageUrl;

    if (!imageUrl) {
      setImgError(true);
      setImgLoading(false);
      return;
    }

    setImgLoading(true);
    const img = new Image();

    img.onload = () => {
      setImgLoading(false);
      setImgError(false);
    };

    img.onerror = () => {
      setImgLoading(false);
      setImgError(true);
    };

    img.src = imageUrl;
  }, [imageSrc, userData?.cards?.data?.imageUrl, card?.imageUrl]);
  return (
    <aside className="w50 p1 card card-sec">
      <div className="flex-between">
        <span className="summary-title">Card Summary</span>
      </div>

      <div className="flex-between">
        {/* LEFT */}
        <div className="w35">
          <div className="highSmall">
            <label>Available balance: </label>

            <span
              className="balance-row"
              onClick={() => setShowBal((b) => !b)}
              style={{ cursor: "pointer" }}
            >
              <BaseCurrency /> {showBal ? millify(card?.balance) : "XXXXX"}{" "}
              {showBal ? (
                <EyeComponent image={eyeOpen} />
              ) : (
                <EyeComponent image={eyeClose} />
              )}
            </span>
          </div>
        </div>

        {/* RIGHT */}
        <div className="w65 flex-end-col">
          <div
            className="debit-card"
            style={{
              position: "relative",
              backgroundImage:
                !imgError && !imgLoading
                  ? `url(${imageSrc || userData?.cards?.data?.imageUrl || card?.imageUrl})`
                  : "none",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
            onClick={() =>
              history.push(`/card-details/${card.id}`, {
                cardData: card,
                isVertical: false,
              })
            }
          >
            {imgLoading && (
              <div
                className="card-image-placeholder"
                aria-hidden
                style={{ position: "absolute", inset: 0 }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "#f5f5f6",
                    borderRadius: 8,
                  }}
                />
              </div>
            )}

            {imgError && (
              <div
                className="card-image-fallback"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#999",
                  borderRadius: 5,
                  background: "#f5f5f6",
                }}
              >
                Card image unavailable
              </div>
            )}

            <div className="mt1 ref">
              {/* CARD NUMBER */}
              <span className="profileCardRightSideCardNumber">
                {visibleField === "cardNumber"
                  ? formatNumber(sensitiveData?.response?.cardNumber || "")
                  : maskCardNumber(sensitiveData?.response?.cardNumber)}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setVisibleField(
                      visibleField === "cardNumber" ? null : "cardNumber",
                    );
                  }}
                >
                  {visibleField === "cardNumber" ? (
                    <EyeComponent image={eyeOpen} />
                  ) : (
                    <EyeComponent image={eyeClose} />
                  )}
                </span>
              </span>

              {/* EXPIRY + CVV */}
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                <div className="expiryDate v-center">
                  <span className="label-text">VALID UPTO</span>
                  <span>
                    {visibleField === "expiryDate"
                      ? formatExpiry(sensitiveData?.response?.expiryDate)
                      : maskExpiryDate()}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisibleField(
                          visibleField === "expiryDate" ? null : "expiryDate",
                        );
                      }}
                    >
                      {visibleField === "expiryDate" ? (
                        <EyeComponent image={eyeOpen} />
                      ) : (
                        <EyeComponent image={eyeClose} />
                      )}
                    </span>
                  </span>
                </div>

                <div className="cvv v-center">
                  <span className="label-text">CVV</span>
                  <span>
                    {visibleField === "cvv"
                      ? sensitiveData?.response?.cvv
                      : maskCVV()}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisibleField(visibleField === "cvv" ? null : "cvv");
                      }}
                    >
                      {visibleField === "cvv" ? (
                        <EyeComponent image={eyeOpen} />
                      ) : (
                        <EyeComponent image={eyeClose} />
                      )}
                    </span>
                  </span>
                </div>
              </div>

              <div>
                <span>{card?.employee_details?.name}</span>
              </div>
            </div>
          </div>

          <div className="flex-between max-wd">
            <button
              className="primary-btn"
              onClick={() =>
                history.push(`/card-details/${card.id}`, {
                  cardData: card,
                  viewTxns: true,
                  isVertical: false,
                })
              }
            >
              View Transactions
            </button>
            <button
              className="nm-btn"
              onClick={() =>
                history.push(`/card-details/${card.id}`, {
                  cardData: card,
                  isVertical: false,
                })
              }
            >
              Settings
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
